/**
 * StyleConfigPanel - 自定义字幕样式配置面板
 *
 * 功能：提供表单控件让用户自定义字幕样式
 * 特点：独立组件，包含防抖处理
 */

import React from 'react';
import type { SubtitleStyle, SubtitlePosition } from '../../types';

interface StyleConfigPanelProps {
  /** 当前字幕样式 */
  style: SubtitleStyle;
  /** 样式更新回调（防抖） */
  onStyleChange: (style: SubtitleStyle) => void;
  /** 翻译函数 */
  t?: (key: string) => string;
}

const StyleConfigPanel: React.FC<StyleConfigPanelProps> = ({ style, onStyleChange, t = (key: string) => key }) => {
  /**
   * 更新单个样式属性
   */
  const updateStyle = <K extends keyof SubtitleStyle>(key: K, value: SubtitleStyle[K]) => {
    const newStyle = { ...style, [key]: value };
    onStyleChange(newStyle);
  };

  return (
    <div className="style-config-panel space-y-3">
      {/* 字体设置 */}
      <div className="config-section">
        <h3 className="text-xs font-semibold text-gray-300 mb-2">{t('subtitle.fontSettings')}</h3>

        {/* 字体大小 */}
        <div className="mb-2">
          <label className="block text-xs text-gray-400 mb-1">
            {t('subtitle.fontSize')}: {style.fontSize}px
          </label>
          <input
            type="range"
            min="10"
            max="200"
            value={style.fontSize}
            onChange={(e) => updateStyle('fontSize', Number(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        {/* 加粗、斜体、下划线 */}
        <div className="flex gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={style.bold || false}
              onChange={(e) => updateStyle('bold', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            {t('subtitle.bold')}
          </label>

          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={style.italic || false}
              onChange={(e) => updateStyle('italic', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            {t('subtitle.italic')}
          </label>

          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={style.underline || false}
              onChange={(e) => updateStyle('underline', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            {t('subtitle.underline')}
          </label>
        </div>
      </div>

      {/* 颜色设置 */}
      <div className="config-section">
        <h3 className="text-xs font-semibold text-gray-300 mb-2">{t('subtitle.colorSettings')}</h3>

        {/* 主颜色 */}
        <div className="mb-2">
          <label className="block text-xs text-gray-400 mb-1">{t('subtitle.textColor')}</label>
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={style.primaryColor}
              onChange={(e) => updateStyle('primaryColor', e.target.value)}
              className="w-12 h-8 rounded cursor-pointer"
            />
            <input
              type="text"
              value={style.primaryColor}
              onChange={(e) => updateStyle('primaryColor', e.target.value)}
              className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm text-white"
              placeholder="#FFFFFF"
            />
          </div>
        </div>

        {/* 边框颜色 */}
        <div className="mb-2">
          <label className="block text-xs text-gray-400 mb-1">
            {t('subtitle.outlineColor')} ({t('subtitle.width')}: {style.outlineWidth}px)
          </label>
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={style.outlineColor}
              onChange={(e) => updateStyle('outlineColor', e.target.value)}
              className="w-12 h-8 rounded cursor-pointer"
            />
            <input
              type="text"
              value={style.outlineColor}
              onChange={(e) => updateStyle('outlineColor', e.target.value)}
              className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm text-white"
              placeholder="#000000"
            />
          </div>
          <input
            type="range"
            min="0"
            max="10"
            value={style.outlineWidth}
            onChange={(e) => updateStyle('outlineWidth', Number(e.target.value))}
            className="w-full h-2 mt-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        {/* 背景颜色（可选） */}
        <div>
          <label className="flex items-center gap-2 text-xs text-gray-400 mb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!style.backgroundColor}
              onChange={(e) => {
                // 一次性同时更新两个属性，避免防抖导致的状态覆盖问题
                const newStyle = { ...style };
                if (e.target.checked) {
                  newStyle.backgroundColor = '#000000';
                  newStyle.backgroundOpacity = 0.5;
                } else {
                  newStyle.backgroundColor = undefined;
                  newStyle.backgroundOpacity = undefined;
                }
                onStyleChange(newStyle);
              }}
              className="w-4 h-4 rounded cursor-pointer appearance-auto"
            />
            <span className="select-none">
              {t('subtitle.enableBgColor')}
            </span>
          </label>
          {style.backgroundColor && (
            <div className="flex gap-2 items-center mt-2">
              <input
                type="color"
                value={style.backgroundColor}
                onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                className="w-12 h-8 rounded cursor-pointer"
              />
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={style.backgroundOpacity || 0.5}
                onChange={(e) => updateStyle('backgroundOpacity', Number(e.target.value))}
                className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-xs text-gray-400 w-12">
                {Math.round((style.backgroundOpacity || 0.5) * 100)}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 位置设置 */}
      <div className="config-section">
        <h3 className="text-xs font-semibold text-gray-300 mb-2">{t('subtitle.positionSettings')}</h3>

        {/* 对齐方式 */}
        <div className="mb-2">
          <label className="block text-xs text-gray-400 mb-1">{t('subtitle.alignment')}</label>
          <div className="flex gap-2">
            {(['top', 'center', 'bottom'] as SubtitlePosition[]).map((align) => (
              <button
                key={align}
                onClick={() => updateStyle('alignment', align)}
                className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
                  style.alignment === align
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {align === 'top' ? t('subtitle.top') : align === 'center' ? t('subtitle.center') : t('subtitle.bottom')}
              </button>
            ))}
          </div>
        </div>

        {/* 垂直边距 */}
        <div>
          <label className="block text-xs text-gray-400 mb-1">
            {t('subtitle.verticalMargin')}: {style.marginV}px
          </label>
          <input
            type="range"
            min="0"
            max="400"
            value={style.marginV}
            onChange={(e) => updateStyle('marginV', Number(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
};

export default StyleConfigPanel;
